import {EventEmitter, inject, Injectable, Output} from '@angular/core';
import {Menu} from '../Models/User/Menu';
import {PostLoginUser} from '../Models/User/PostLoginUser';
import {Locale} from '../Models/User/Locale';
import {GraphicUtils} from '@aiknow-srl/aik-angular-sdk';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TranslateService} from '@ngx-translate/core';
import {PostLoginCB} from '../Ope/User/PostLoginCB';
import {environment} from '../../environments/environment';
import {KeyVal} from '../Models/Common/KeyVal';
import jwtDecode, {JwtPayload} from 'jwt-decode';
import {saveAs} from 'file-saver';
import {ProductType} from "../Models/CustomerProductItem/ProductType";

@Injectable({
  providedIn: 'root'
})
export class GlobalModelService {

  readonly APP_NAME = "PS IoT";

  @Output() refreshEvent = new EventEmitter();

  private readonly _EMAIL_PATTERN_VALIDATOR = '^\\w+([\\.-]?\\w+)*@\\w+([\\.-]?\\w+)*(\\.\\w{2,4})+$';
  private readonly _MULTIPLE_EMAIL_PATTERN_VALIDATOR = '^[\\W]*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4}[\\W]*,{1}[\\W]*)*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4})[\\W]*$';

  readonly FILE_MAX_SIZE = 2 * 1024 * 1024;

  private readonly _PUT_CUSTOMER_IMAGE_SERVICE = "putCustomerImage";
  private readonly _PUT_DEVICE_IMAGE_SERVICE = "putCustomerDeviceImage";
  private readonly _PUT_PRODUCTITEM_IMAGE_SERVICE = "putCustomerProductItemImage";

  private readonly _PUT_ASSET_IMAGE_SERVICE = "putCustomerAssetImage";

  /**
   * Mobile screen width threshold
   */
  public readonly MOBILE_SCREEN_THRESHOLD = 570;
  /**
   * Small screen width threshold
   */
  public readonly SMALL_SCREEN_THRESHOLD = 850;
  /**
   * Medium screen width threshold
   */
  public readonly MEDIUM_SCREEN_THRESHOLD = 1200;
  /**
   * Big screen width threshold
   */
  public readonly BIG_SCREEN_THRESHOLD = 1800;


  // ----------------------------------
  //   POST LOGIN PARAMS
  // ----------------------------------
  /**
   * Application Id
   */
  private _appId?: string;
  /**
   * Environment type
   */
  private _envId?: string;
  /**
   * Application version
   */
  private _appVersion?: string;
  /**
   * Environment Label type
   */
  private _envIdLabel?: string;
  /**
   * Flag che indica se il servizio di OSM è abilitato o meno
   */
  private _osmEnabled!: boolean;
  /**
   * Logged User
   */
  private _user?: PostLoginUser;
  /**
   * Languages list
   */
  private _localeList?: Locale[];

  private _productTypesEnum!: ProductType[];
  private _assetTypesEnum!: KeyVal[];

  private _deviceRegistryStatusEnum!: KeyVal[];


  // ****************************
  //      SNACKBAR PARAMS
  // ****************************
  /**
   * Undefined duration of snackbar
   */
  private readonly UNDEFINED_SNACKBAR_DURATION = undefined;
  /**
   * 5 sec duration of snackbar
   */
  private readonly TWO_SECONDS_SNACKBAR_DURATION = 2000;
  /**
   * Horizontal sknackbar position
   */
  private readonly HORIZONTAL_SNACKBAR_POSITION = 'center';
  /**
   * Vertical sknackbar position
   */
  private readonly VERTICAL_SNACKBAR_POSITION = 'bottom';
  /**
   * This number represents the number of days kept as telemetries for the graph.
   * This number is used in product dashboard and in archive registry.
   */
  readonly DAYS_TELEMETRY = 70;



  public menuVoice?: Menu;

  private messageSnackBar: MatSnackBar = inject(MatSnackBar);
  private translate: TranslateService = inject(TranslateService);


  // ***************************************
  //      PostLogin Getters and Setters
  // ***************************************
  /**
   * Getter for _appId
   */
  get appId(): string {
    // @ts-ignore
    return this._appId;
  }
  /**
   * Setter for _appId
   */
  set appId(value: string) {
    this._appId = value;
  }
  /**
   * Getter for _envId
   */
  get envId(): string {
    // @ts-ignore
    return this._envId;
  }
  /**
   * Setter for _envId
   */
  set envId(value: string) {
    this._envId = value;
  }
  /**
   * Getter for _appVersion
   */
  get appVersion(): string {
    // @ts-ignore
    return this._appVersion;
  }
  /**
   * Setter for _appVersion
   */
  set appVersion(value: string) {
    this._appVersion = value;
  }
  /**
   * Getter for _envIdlabel
   */
  get envIdLabel(): string {
    // @ts-ignore
    return this._envIdLabel;
  }
  /**
   * Setter for _envIdlabel
   */
  set envIdLabel(value: string) {
    this._envIdLabel = value;
  }

  /**
   * setter for _osmEnabled
   */
  set osmEnabled(value: boolean) {

  }

  /**
   * getter for _osmEnabled
   */
  get osmEnabled(): boolean {
    return this._osmEnabled;
  }

  /**
   * Getter for _user
   */
  get user(): PostLoginUser {

    // @ts-ignore
    return this._user;
  }
  /**
   * Setter for _user
   */
  set user(value: PostLoginUser) {
    this._user = value;
  }
  /**
   * Getter for _localeList
   */
  get localeList(): Locale[] {
    // @ts-ignore
    return this._localeList;
  }
  /**
   * Setter for _localeList
   */
  set localeList(value: Locale[]) {
    this._localeList = value;
  }

  get productTypesEnum(): ProductType[] {
    return this._productTypesEnum;
  }

  set productTypesEnum(value: ProductType[]) {
    this._productTypesEnum = value;
  }
  get deviceRegistryStatusEnum(): KeyVal[] {
    return this._deviceRegistryStatusEnum;
  }

  set deviceRegistryStatusEnum(value: KeyVal[]) {
    this._deviceRegistryStatusEnum = value;
  }

  get EMAIL_PATTERN_VALIDATOR(): string {
    return this._EMAIL_PATTERN_VALIDATOR;
  }

  get MULTIPLE_EMAIL_PATTERN_VALIDATOR(): string {
    return this._MULTIPLE_EMAIL_PATTERN_VALIDATOR;
  }

  get PUT_CUSTOMER_IMAGE_SERVICE(): string {
    return this._PUT_CUSTOMER_IMAGE_SERVICE;
  }

  get PUT_DEVICE_IMAGE_SERVICE(): string {
    return this._PUT_DEVICE_IMAGE_SERVICE;
  }

  get PUT_PRODUCTITEM_IMAGE_SERVICE(): string {
    return this._PUT_PRODUCTITEM_IMAGE_SERVICE;
  }

  get PUT_ASSET_IMAGE_SERVICE(): string {
    return this._PUT_ASSET_IMAGE_SERVICE;
  }

  // ----------------------------------
  //     GLOBAL APP METHODS
  // ----------------------------------
  /**
   * The method is used to store in the Global Model Service all the params in the postLogin service response
   * @param cb the GetTransportItemDetailCB object
   */
  public storeParamsIntoGlobalModel(cb: PostLoginCB): void {
    this.appId = cb.o.appId;
    this.envId = cb.o.envId;
    this.appVersion = cb.o.appVersion;
    this.envIdLabel = cb.o.envIdLabel;
    this.osmEnabled = cb.o.osmEnabled;
    this.user = cb.o.user;
    this.localeList = cb.o.localeList;
    this.deviceRegistryStatusEnum = cb.o.deviceRegistryStatusEnum;
    this.setCountryFlag();

    // ADD USER FOR BUGSNAG REPORTS
    // Bugsnag.addOnError(event => event.setUser('', this.customerUser.username, this.customerUser.username));
    //
    // Bugsnag.addOnError(event => event.addMetadata('APP', {version: this.appVersion}));

  }

  /**
   * Set country flag basing on locale
   */
  private setCountryFlag(): void{
    for (const locale of this.localeList) {
      if (locale.locale === 'it') {
        locale.countryFlag = 'it';
      } else if (locale.locale === 'en') {
        locale.countryFlag = 'gb';
      } else if (locale.locale === 'de') {
        locale.countryFlag = 'de';
      } else if (locale.locale === 'es') {
        locale.countryFlag = 'es';
      }
    }
  }

  public isTenantAdmin(): boolean {

    const flag = this.user.roles?.includes('R_TENANT_ADMIN');

    return flag ? flag : false;
  }

  public isTenantUser(): boolean {
    const flag = this.user.roles?.includes('R_TENANT_USER');

    return flag ? flag : false;
  }


  /**
   * The method composes the base URL of the BE
   */
  public composeUrl(): string {
    return `${environment.APIProtocol}${environment.APIIp}:${environment.APIPort}${environment.APIContext}`;
  }

  /**
   * The method composes the base URL of the BE
   */
  public composePutImageUrl(serviceName: string): string {
    return `${this.composeUrl()}/${serviceName}`;
  }

  public downloadFile(fileName: string, mimeType: string, binary: string): void {

    const urlFile = this.composeFileUrlFromBase64(mimeType, binary);

    fetch(urlFile).then((res) => res.arrayBuffer()).then((buf) => {
      const file = new File([buf], fileName,{type:mimeType});

      saveAs(file, fileName);
    })
  }

  private composeFileUrlFromBase64(mimeType: string, binary: string) {
    return `data:${mimeType};base64,${binary}`;
  }

  printDateInBeFormat(date: Date | null | undefined): string | undefined {
    let result = undefined;

    if (!!date) {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();

      result = `${year}-${month}-${day}`;
    }

    return result;
  }

  // *******************************
  //      MAT SNACKBAR METHODS
  // *******************************
  /**
   * The method opens a snackBar with success message and style.
   * The default duration is 2 seconds
   * The default position is bottom-centered
   * @param messageID the message to display
   */
  public openTranslatedSuccessSnackbar(messageID: string): void  {
    this.openSuccessSnackbar(this.translate.instant(messageID));
  }
  public openSuccessSnackbar(message: string): void  {
    this.messageSnackBar.open(message, 'Chiudi', {
      duration: this.TWO_SECONDS_SNACKBAR_DURATION,
      panelClass: 'successSnackBar',
      horizontalPosition: this.HORIZONTAL_SNACKBAR_POSITION,
      verticalPosition: this.VERTICAL_SNACKBAR_POSITION,
    });
  }

  /**
   * The method opens a snackBar with error message and style.
   * The default duration is undefined
   * The default position is bottom-centered
   * @param errorID the error to display
   */
  public openTranslatedErrorSnackbar(errorID: string): void  {
    this.openErrorSnackbar(this.translate.instant(errorID));
  }

  public openInfoSnackbar(msg: string): void  {

    this.messageSnackBar.open(msg, 'Chiudi', {
      duration: this.UNDEFINED_SNACKBAR_DURATION,
      panelClass: ['infoSnackBar', 'mat-typography'],
      horizontalPosition: this.HORIZONTAL_SNACKBAR_POSITION,
      verticalPosition: this.VERTICAL_SNACKBAR_POSITION,
    });
  }

  public openErrorSnackbar(error1: string): void  {

    this.messageSnackBar.open(error1, 'Chiudi', {
      duration: this.UNDEFINED_SNACKBAR_DURATION,
      panelClass: ['errorSnackBar', 'mat-typography'],
      horizontalPosition: this.HORIZONTAL_SNACKBAR_POSITION,
      verticalPosition: this.VERTICAL_SNACKBAR_POSITION,
    });
  }

  public closeSnackbar(): void {
    this.messageSnackBar.dismiss();
  }


  // *******************************************

  isMobileScreen() {
    return GraphicUtils.isScreenWidthGreater(this.MOBILE_SCREEN_THRESHOLD);
  }

  isSmallScreen() {
    return GraphicUtils.isScreenWidthGreater(this.SMALL_SCREEN_THRESHOLD);
  }


  // ********************************************


  getTokenExpiration(token: string): number | null {
    const tokenDecoded = jwtDecode<JwtPayload>(token);

    const iat = tokenDecoded.iat;
    const exp = tokenDecoded.exp;

    if (!!iat && !!exp) {
      const ttl = exp - iat;
      if (ttl > 0) {
        return new Date().valueOf() + ttl * 1000;
      }
    }
    return null;
  }

  fillRequestWithParameters(...strings: string[]): string{
    return '?' + strings.reduce((a, b) => `${a}&${b}`);
  }

  redirectToThingsBoard(access_token: string) {
    const atExp = this.getTokenExpiration(access_token);

    if (!!atExp) {
      let link = 'https://telecontrollo.pradella.it/redirect/';

      link += this.fillRequestWithParameters(`jwt_token=${access_token}`, `jwt_token_expiration=${String(atExp)}`);
      // link += this.fillRequestWithParameters(`jwt_token=${access_token}`, `jwt_token_expiration=${String(atExp)}`,
      //   `refresh_token=${this.be.getRefreshToken()}`, `refresh_token_expiration=${String(rtExp)}`);

      window.open(link, '_blank');

    } else {
      console.error('Token is not valid');
    }
  }
}
